export const ALLOWED_UTMS = [
    // Identify the advertiser, site, publication, etc. that is sending traffic to your property,
    // for example: google, newsletter4, billboard.
    'utm_source',
    // The advertising or marketing medium, for example: cpc, banner, email newsletter.
    'utm_medium',
    // The individual campaign name, slogan, promo code, etc. for a product.
    'utm_campaign',
    // Identify paid search keywords. If you're manually tagging paid keyword campaigns,
    // you should also use utm_term to specify the keyword.
    'utm_content',
    // Used to differentiate similar content, or links within the same ad. For example,
    // if you have two call-to-action links within the same email message, you can use utm_content
    // and set different values for each so you can tell which version is more effective.
    'utm_term'
];

export type TUtm = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term';

export type IUtm = {
    [key in TUtm]: string;
};

export const filterValidUtms = utms => {
    return Object.keys(utms)
        .filter(k => ALLOWED_UTMS.includes(k))
        .reduce((o, k) => {
            o[k] = utms[k];
            return o as IUtm;
        }, {} as IUtm);
};

/**
 * Get utms from link
 *
 * @example
 * ```typescript
 *  utm('https://www.google.com?utm_source=google&utm_medium=cpc')
 *
 * // output: { utm_source: 'google', utm_medium: 'cpc' }
 * ```
 *
 * @param link - link to parse
 * @param sep - separator
 * @returns {IUtm} - utms
 */
export function utm(link: string, sep = '&|#'): IUtm {
    const parsed = new URL(link);
    const href = parsed.href || '';

    const utmRegx = new RegExp('((utm_[^=]+)=([^\\' + sep + ']+))', 'gi');
    const matched = href.match(utmRegx) || [];

    return (matched.reduce((previouseValue: never, currentValue: string) => {
        const [utm, value]: string[] = currentValue.split('=');

        (<IUtm>previouseValue)[utm] = value;

        return previouseValue;
    }, {} as never) || {}) as IUtm;
}

export function strict(link: string, sep = '&|#') {
    const utms = utm(link, sep);

    return filterValidUtms(utms);
}

/**
 * Build utm link
 *
 * @example
 * ```typescript
 *   build('https://www.google.com', { utm_source: 'google', utm_medium: 'cpc' }, true)
 *
 *   // output: https://www.google.com?utm_source=google&utm_medium=cpc
 * ```
 *
 * @param link - link to build
 * @param utms - utms to add
 * @param isStrict - if true, only valid utms will be added
 * @returns {URL} - built url
 */
export function build(link: string, utms: IUtm, isStrict: boolean): URL {
    const url = new URL(link);
    utms = isStrict ? filterValidUtms(utms) : utms;

    Object.keys(utms).forEach(key => {
        url.searchParams.append(key, utms[key]);
    });

    return url;
}
